import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { ApiError } from "./useGetAllApplications";
import { ExtendedWebhook } from "./useGetOneApplication";

const jsonHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
};

export interface WebhookData {
    active?: boolean;
    name?: string;
    method?: string;
    headers?: Record<string, string>;
    retryCount?: number;
    endpoint?: string;
    eventName?: string;
    serializationGroups?: Array<string> | null;
}

type CreateWebhookOptions = {
    applicationId: string;
    webhook: WebhookData;
};

const toWebhookFormError = (error: ApiError) => {
    if (error.response?.status === 422) {
        const validationErrors = error.response.data.violations;
        if (validationErrors && Array.isArray(validationErrors)) {
            return validationErrors.reduce(
                (acc, violation) => ({
                    ...acc,
                    [violation.propertyPath]: violation.message,
                }),
                {},
            );
        }
    }
    return error.message ? { name: error.message } : new Error("Failed to create webhook");
};

export const useCreateWebhook = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation<ExtendedWebhook, ApiError, CreateWebhookOptions>(
        async (options: CreateWebhookOptions) => {
            const { applicationId, webhook } = options;

            // Ensure we have the required fields
            if (!webhook.endpoint) {
                throw new Error("Either endpoint or url is required");
            }

            if (!webhook.eventName) {
                throw new Error("eventName is required");
            }

            if (!webhook.name) {
                throw new Error("Webhook name is required");
            }

            // Create the API webhook object with proper types
            const apiWebhook = {
                endpoint: String(webhook.endpoint || ""),
                eventName: webhook.eventName ? String(webhook.eventName) : "",
                active: webhook.active !== undefined ? Boolean(webhook.active) : true,
                name: String(webhook.name || ""),
                method: webhook.method ? String(webhook.method) : "POST",
                headers: webhook.headers || {},
                retryCount: webhook.retryCount !== undefined ? Number(webhook.retryCount) : 3,
                application: `/api/integrations/applications/${applicationId}`,
                serializationGroups: webhook.serializationGroups ?? null,
            };

            try {
                const response = await loggedInApiCall.post(
                    `/api/integrations/applications/${applicationId}/webhooks`,
                    apiWebhook,
                    {
                        headers: jsonHeaders,
                    },
                );

                return response.data;
            } catch (error) {
                throw toWebhookFormError(error as ApiError);
            }
        },
        {
            onSuccess: (data, { applicationId }) => {
                queryClient.invalidateQueries(["applicationWebhooks", applicationId]);
            },
        },
    );

    return mutation;
};
